import { createContext, useState, useEffect } from "react";
import axios from "axios";

const AuthContext = createContext();

const isProduction = process.env.NODE_ENV === "production";
const API_BASE_URL = isProduction
    ? process.env.REACT_APP_API_BASE_URL_PROD
    : process.env.REACT_APP_API_BASE_URL_DEV;

export const AuthProvider = ({ children }) => {
    const [token, setToken] = useState(localStorage.getItem("token") || null);
    const [user, setUser] = useState(null);
    const [isAuthInitializing, setIsAuthInitializing] = useState(Boolean(localStorage.getItem("token")));
    const [authError, setAuthError] = useState(null);

    useEffect(() => {
        if (token) {
            localStorage.setItem("token", token);
        } else {
            localStorage.removeItem("token");
            setUser(null);
            setAuthError(null);
            setIsAuthInitializing(false);
        }
    }, [token]);

    useEffect(() => {
        let isMounted = true;
        const controller = new AbortController();

        const restoreSession = async () => {
            if (!token) {
                if (isMounted) {
                    setIsAuthInitializing(false);
                }
                return;
            }

            if (user) {
                if (isMounted) {
                    setIsAuthInitializing(false);
                }
                return;
            }

            if (isMounted) {
                setIsAuthInitializing(true);
            }

            try {
                const response = await axios.get(`${API_BASE_URL}/auth/check-user`, {
                    headers: { Authorization: `Bearer ${token}` },
                    signal: controller.signal,
                });

                if (!isMounted) {
                    return;
                }

                setUser(response.data);
                setAuthError(null);
            } catch (error) {
                if (!isMounted || error.code === "ERR_CANCELED") {
                    return;
                }

                const status = error.response?.status;
                const message = error.response?.data?.message || "Unable to restore session";

                if (status === 401 || status === 403 || status === 420) {
                    setToken(null);
                    setUser(null);
                }

                setAuthError(message);
            } finally {
                if (isMounted) {
                    setIsAuthInitializing(false);
                }
            }
        };

        restoreSession();

        return () => {
            isMounted = false;
            controller.abort();
        };
    }, [token, user]);

    const refreshUser = async () => {
        if (!token) {
            setUser(null);
            setIsAuthInitializing(false);
            return null;
        }

        setIsAuthInitializing(true);

        try {
            const response = await axios.get(`${API_BASE_URL}/auth/check-user`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            setUser(response.data);
            setAuthError(null);
            return response.data;
        } catch (error) {
            const status = error.response?.status;
            const message = error.response?.data?.message || "Unable to refresh session";

            if (status === 401 || status === 403 || status === 420) {
                setToken(null);
                setUser(null);
            }

            setAuthError(message);
            throw error;
        } finally {
            setIsAuthInitializing(false);
        }
    };

    const login = (nextToken, nextUser = null) => {
        setIsAuthInitializing(true);
        setAuthError(null);
        setToken(nextToken);
        setUser(nextUser);
    };

    const logout = () => {
        setToken(null);
        setUser(null);
        setAuthError(null);
        setIsAuthInitializing(false);
    };

    return (
        <AuthContext.Provider
            value={{
                token,
                user,
                setUser,
                login,
                logout,
                refreshUser,
                isAuthInitializing,
                authError,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContext;
